import { useCallback, useEffect, useMemo, useState } from "react";
import { CanceledError } from "axios";
import { useToast } from "@/components/ui/use-toast";
import {
  AdminCatalogCategory,
  fetchCatalogCategories,
} from "../api/fetchCatalogCategories";
import {
  SaveCatalogCategoryRequest,
  createCatalogCategory,
} from "../api/createCatalogCategory";
import { updateCatalogCategory } from "../api/updateCatalogCategory";
import { deleteCatalogCategory } from "../api/deleteCatalogCategory";
import { createCatalogCategoryColumns } from "../components/CatalogCategoryColumns/CatalogCategoryColumns";

export function useCatalogCategoryList() {
  const { toast } = useToast();
  const [categories, setCategories] = useState<AdminCatalogCategory[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [dialogState, setDialogState] = useState({
    isOpen: false,
    category: null as AdminCatalogCategory | null,
    isSaving: false,
  });
  const [deleteDialogState, setDeleteDialogState] = useState({
    isOpen: false,
    categoryId: null as string | null,
    categoryName: "",
  });

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchCatalogCategories(
      {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
      },
      controller.signal,
    )
      .then((response) => {
        const total = response.meta?.totalCount ?? response.data.length;
        // Same clamp as the products list: never strand the table past the
        // last page after a delete.
        if (
          response.data.length === 0 &&
          total > 0 &&
          pagination.pageIndex > 0
        ) {
          setPagination((prev) => ({
            ...prev,
            pageIndex: Math.max(0, Math.ceil(total / prev.pageSize) - 1),
          }));
          return;
        }
        setCategories(response.data);
        setTotalCount(total);
        setLoading(false);
      })
      .catch((err) => {
        if (err instanceof CanceledError) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [pagination, refreshCounter]);

  const refreshList = useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  const openCreateDialog = useCallback(() => {
    setDialogState({ isOpen: true, category: null, isSaving: false });
  }, []);

  const openEditDialog = useCallback((category: AdminCatalogCategory) => {
    setDialogState({ isOpen: true, category, isSaving: false });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState({ isOpen: false, category: null, isSaving: false });
  }, []);

  const handleSave = useCallback(
    async (data: SaveCatalogCategoryRequest) => {
      setDialogState((prev) => ({ ...prev, isSaving: true }));

      const request = dialogState.category
        ? updateCatalogCategory(dialogState.category.id, data)
        : createCatalogCategory(data);

      await request
        .then(() => {
          toast({
            title: "Success",
            description: dialogState.category
              ? "Category updated"
              : "Category created",
            variant: "default",
          });
          closeDialog();
          refreshList();
        })
        .finally(() =>
          setDialogState((prev) => ({ ...prev, isSaving: false })),
        );
    },
    [dialogState.category, closeDialog, refreshList, toast],
  );

  const handleDeleteCategory = useCallback((category: AdminCatalogCategory) => {
    setDeleteDialogState({
      isOpen: true,
      categoryId: category.id,
      categoryName: category.name,
    });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogState({
      isOpen: false,
      categoryId: null,
      categoryName: "",
    });
  }, []);

  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialogState.categoryId || isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteCatalogCategory(deleteDialogState.categoryId);
      closeDeleteDialog();
      refreshList();
      toast({
        title: "Success",
        description: "Category deleted",
        variant: "default",
      });
    } catch {
      // Global interceptor already toasted the failure (e.g. "category has
      // products assigned") — keep the dialog open so the admin sees the
      // delete did not happen.
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteDialogState.categoryId,
    isDeleting,
    closeDeleteDialog,
    refreshList,
    toast,
  ]);

  const columns = useMemo(
    () => createCatalogCategoryColumns(openEditDialog, handleDeleteCategory),
    [openEditDialog, handleDeleteCategory],
  );

  return {
    categories,
    totalCount,
    loading,
    pagination,
    setPagination,
    columns,
    dialogState,
    openCreateDialog,
    closeDialog,
    handleSave,
    deleteDialogState,
    closeDeleteDialog,
    handleConfirmDelete,
    isDeleting,
  };
}
